import { useSelector, useDispatch } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import { DesignSystemToken, StringTokenValue } from 'designSystem/types';
import { resolvesToColor, formatTokenValue } from './tokenUtil';
import * as S from './styles';

interface TokenTableProps {
	tokens: DesignSystemToken[];
}

// Extract CSS variable name from var() reference
function extractVariableName(varReference: string): string {
	const match = varReference.match(/var\((--[^,)]+)/);
	return match ? match[1] : varReference;
}

// Renders a small inline preview appropriate for the token kind
const InlinePreview = ({ token }: { token: DesignSystemToken }) => {
	// Color tokens (hex)
	if (token.value.kind === 'color') {
		return <S.TableInlineSwatch $color={token.value.hex} />;
	}

	// String tokens that resolve to colors (via var())
	const isColor = token.category === 'color' || resolvesToColor(token.value.original);
	if (token.value.kind === 'string' && isColor) {
		const varName = extractVariableName((token.value as StringTokenValue).original);
		return <S.TableInlineVarSwatch style={{ backgroundColor: `var(${varName})` }} />;
	}

	// Shadows
	if (token.value.kind === 'shadow') {
		const shadow = `${token.value.offsetX}px ${token.value.offsetY}px ${token.value.blur}px ${
			token.value.spread || 0
		}px ${token.value.color.hex}`;
		return <S.TableInlineShadow $shadow={shadow} />;
	}

	// Border radius
	if (token.value.kind === 'borderRadius') {
		const px = token.value.length.px ?? token.value.length.numericValue * 16;
		return <S.TableInlineRadius $radius={Math.min(px, 14)} />;
	}

	// Spacing / length
	if (token.value.kind === 'length') {
		const px = token.value.px ?? token.value.numericValue * 16;
		return <S.TableInlineBar $width={px} />;
	}

	// Font size
	if (token.value.kind === 'fontSize') {
		const px = token.value.length.px ?? token.value.length.numericValue * 16;
		return <S.TableInlineText $fontSize={px}>Aa</S.TableInlineText>;
	}

	// Font weight
	if (token.value.kind === 'fontWeight') {
		return <S.TableInlineText $fontWeight={token.value.numeric}>Aa</S.TableInlineText>;
	}

	// Font family
	if (token.value.kind === 'fontFamily') {
		return (
			<S.TableInlineText $fontFamily={token.value.families[0]}>Aa</S.TableInlineText>
		);
	}

	// Opacity
	if (token.value.kind === 'opacity') {
		return (
			<S.TableInlineSwatch
				$color={`rgba(0,0,0,${token.value.value})`}
			/>
		);
	}

	return <S.TableEmptyPreview />;
};

const categoryLabels: Record<string, string> = {
	color: 'Color',
	typography: 'Type',
	spacing: 'Space',
	elevation: 'Elevation',
	borderRadius: 'Radius',
	opacity: 'Opacity',
	motion: 'Motion',
	other: 'Other',
};

// Unified list view for any token type. Replaces the old ColorTable.
const TokenTable = ({ tokens }: TokenTableProps) => {
	const dispatch = useDispatch();
	const selectedPicks = useSelector((r: RootReducer) => r.mode.selectedDsPicks);

	const isSelected = (token: DesignSystemToken) =>
		selectedPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	return (
		<S.TableBase>
			{tokens.map((token) => (
				<S.TableRow
					key={token.id}
					type="button"
					$selected={isSelected(token)}
					aria-pressed={isSelected(token)}
					onClick={() =>
						dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }))
					}
				>
				<InlinePreview token={token} />
				<S.TableName>{token.name}</S.TableName>
				<S.TableVariable>{token.cssVariable}</S.TableVariable>
				<S.TableValue>{formatTokenValue(token.value)}</S.TableValue>
				</S.TableRow>
			))}
		</S.TableBase>
	);
};

export default TokenTable;
